import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Mail, MessageSquare, PartyPopper, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MergeFieldTextInput } from './MergeFieldTextInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const list = (a) => a.join(' and ');

const ResultDialog = ({ result, onClose }) => (
  <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-lg" data-testid="sc-welcome-result-dialog">
      <DialogHeader>
        <DialogTitle>Welcome messages sent</DialogTitle>
        <DialogDescription data-testid="sc-welcome-result-summary">
          Sent to {result?.sent || 0} participant{result?.sent === 1 ? '' : 's'}.{result?.already_received ? ` ${result.already_received} already had it and ${result.already_received === 1 ? 'was' : 'were'} skipped.` : ''}
        </DialogDescription>
      </DialogHeader>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {result?.skipped?.length > 0 ? (
          <div className="space-y-2" data-testid="sc-welcome-skipped-list">
            <p className="text-sm font-medium">Skipped</p>
            {result.skipped.map((s, i) => (
              <div key={i} className="text-sm rounded-md border p-2" data-testid={`sc-welcome-skipped-${i}`}>
                <p className="font-medium">{s.participant_name}</p>
                {s.reasons.map((r, j) => <p key={j} className="text-xs text-muted-foreground">{r}</p>)}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No one was skipped.</p>}
      </div>
      <DialogFooter className="shrink-0"><Button onClick={onClose} data-testid="sc-welcome-result-close">Close</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

export const SuccessCoachingWelcomeEditor = ({ value, mergeFields, onChange, readOnly, onSave }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [result, setResult] = useState(null);

  const start = async () => {
    setBusy(true);
    try {
      if (!(await onSave())) return;
      const { data } = await axios.get(`${API}/settings/success-coaching-welcome/eligible`);
      if (data.missing_templates.length) { toast.error(`Nothing was sent. The welcome ${list(data.missing_templates)} template${data.missing_templates.length > 1 ? 's are' : ' is'} empty.`); return; }
      if (!data.to_send) { toast.info('Everyone eligible has already received the welcome.'); return; }
      setConfirm(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check eligible participants');
    } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/settings/success-coaching-welcome/send`);
      setConfirm(null); setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send the welcome');
    } finally { setBusy(false); }
  };

  return (
    <Card className="card-shadow border-green-200" data-testid="sc-welcome-templates">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><PartyPopper className="w-4 h-4 text-green-600" />Welcome to Success Coaching Templates</CardTitle>
            <CardDescription>Sent once to each participant eligible for Success Coaching, automatically when a new SNAP E&T enrollment or case is created.</CardDescription>
          </div>
          {!readOnly && (
            <Button variant="outline" onClick={start} disabled={busy} data-testid="sc-welcome-send-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send Welcome to Eligible Participants
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium flex items-center gap-1"><Mail className="w-4 h-4" />Email to participant</p>
          <MergeFieldTextInput label="Subject" value={value.email_subject} mergeFields={mergeFields} disabled={readOnly}
            onChange={set('email_subject')} testId="sc-welcome-email-subject" />
          <MergeFieldTextInput label="Body" multiline rows={6} value={value.email_body} mergeFields={mergeFields} disabled={readOnly}
            onChange={set('email_body')} testId="sc-welcome-email-body" />
        </div>
        <div className="space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium flex items-center gap-1"><MessageSquare className="w-4 h-4" />Text (SMS) to participant</p>
          <MergeFieldTextInput label="Message" multiline rows={6} value={value.sms_body} mergeFields={mergeFields} disabled={readOnly}
            onChange={set('sms_body')} testId="sc-welcome-sms-body"
            footer={<p className="text-[11px] text-muted-foreground" data-testid="sc-welcome-sms-length">{(value.sms_body || '').length} characters (before merge fields are filled in)</p>} />
        </div>
      </CardContent>
      <Dialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="sc-welcome-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Send the welcome?</DialogTitle>
            <DialogDescription data-testid="sc-welcome-confirm-count">
              {confirm?.to_send} participant{confirm?.to_send === 1 ? '' : 's'} on the latest Caseload Review will receive the welcome email and text (only what's possible if an email or phone number is missing).
              {confirm?.already_received ? ` ${confirm.already_received} already received it and will be skipped.` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} data-testid="sc-welcome-confirm-cancel">Cancel</Button>
            <Button onClick={send} disabled={busy} data-testid="sc-welcome-confirm-send">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ResultDialog result={result} onClose={() => setResult(null)} />
    </Card>
  );
};
